import { useDashboardVariationConfig } from '@/pages/DashboardPage/containers/variations/config';
import { AxleV2Theme } from '@/theme';
import { DashboardVariation } from '@/types/organization';
import { gql, useMutation, useSuspenseQuery } from '@apollo/client';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import ContentCopy from '@mui/icons-material/ContentCopy';
import {
    Box,
    Button,
    ButtonProps,
    Collapse,
    Divider,
    IconButton,
    Link,
    ThemeProvider,
    Typography,
    TypographyProps,
} from '@mui/material';
import moment from 'moment';
import { type ComponentProps, type ReactNode, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DialogType, useDialog } from '../../../dialogs/DialogManager';
import { useRedirectIfNotFound } from '../../../hooks/useRedirectIfNotFound';
import { EDIT_PATIENT_NOTES_MUTATION } from '../../../pages/PatientDetailsPage/PatientPreferencesTab';
import { IExternalId } from '../../../types/externalId';
import { Language, renderLanguage } from '../../../types/language';
import { IOrderFrequency, IOrderPrn, OrderType } from '../../../types/order';
import { Sex, renderSex } from '../../../types/sex';
import { AddressInTwoLines, IBasicAddress, basicAddressToAddress } from '../../../utils/addresses';
import { WeekdayTimeWindow } from '../../../utils/datetimes/weekdays';
import { formatPhoneNumberForDisplay } from '../../../utils/formatting';
import { useConfigValue } from '../../../utils/useConfigValue';
import { copyToClipboard } from '../../buttons/CopyButton';
import { DocumentViewerAndDropzone, IDocumentListItem } from '../../displayFields/documents';
import { CaptionLabel } from '../../labels/CaptionLabel';
import { NoneLabel, NoneLabelProps } from '../../labels/NoneLabel';
import { NotesEditor } from '../../notes/NotesDisplay';
import { HorizontalStack, VerticalStack } from '../../utils/linearLayouts';
import { PatientTimeslotPreferenceChipsStack } from '../PatientTimeslotPreferenceChip';
import { NorthStarPatientStatusTag } from '../northStarPatientStatus';
import {
    CareTeamClinicianFragment,
    ICareTeamClinician,
    PatientCareTeamInfo,
} from './PatientCareTeamInfo';
import {
    IPatientLeftRailEpisode,
    PatientLeftRailEpisodeFragment,
    PatientLeftRailEpisodeSection,
} from './PatientLeftRailEpisodeSection';

export const PATIENT_LEFT_RAIL_QUERY = gql`
    query PatientLeftRail($patientId: UUID!) {
        organizations {
            totalCount
        }
        patient(id: $patientId) {
            id
            organization {
                id
                name
                integrations
            }
            createdAt
            fullName
            firstName
            lastName
            phoneNumber
            email
            dateOfBirth
            externalId
            externalLink
            externalIds {
                ...ExternalIdFragment
            }
            sex
            preferredClinicianSex
            linkToPatientVisits(includeQueryParams: false)
            notes
            notesForClinician
            preferredLanguage
            timezone
            defaultEpisode {
                ...PatientLeftRailEpisodeFragment
            }
            careTeams {
                id
                clinicians {
                    ...CareTeamClinicianFragment
                }
            }
            payers {
                id
                name
                description
            }
            preferredTimeslots {
                day
                startTime
                endTime
            }
            addresses {
                ...BasicAddressFragment
            }
            permissions {
                id
                callPatient
                editDetails
                editCareTeam
                viewCommunications
                sendCommunications
                viewDocuments
                editDocuments
                viewInternalNotes
                editInternalNotes
                viewNotesForClinician
                editNotesForClinician
            }
            documents {
                ...DocumentListItemFragment
            }
        }
    }
    ${CareTeamClinicianFragment}
    ${PatientLeftRailEpisodeFragment}
`;

type IOrderDiscipline = {
    id: string;
    code: string;
};
export interface IOrder {
    id: string;
    orderType: OrderType;
    episode: {
        id: string;
    } | null;
    discipline: IOrderDiscipline | null;
    frequency: IOrderFrequency | null;
    prn: IOrderPrn | null;
}

interface IPatientInfo {
    id: string;
    createdAt: Date;
    fullName: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    dateOfBirth: Date;
    externalId: string;
    externalIds: IExternalId[] | null;
    externalLink: string | null;
    sex: Sex;
    preferredClinicianSex: Sex | null;
    notes: string | null;
    notesForClinician: string | null;
    preferredLanguage: Language;
    linkToPatientVisits: string | null;
    organization: {
        id: string;
        name: string;
        integrations: Record<string, string | null>;
    };
    careTeams: {
        id: string;
        clinicians: ICareTeamClinician[];
    }[];
    defaultEpisode: IPatientLeftRailEpisode | null;
    payers: {
        id: string;
        name: string;
        description: string;
    }[];
    addresses: IBasicAddress[];
    permissions: {
        id: string;
        callPatient: boolean;
        editDetails: boolean;
        editCareTeam: boolean;
        viewCommunications: boolean;
        sendCommunications: boolean;
        viewDocuments: boolean;
        editDocuments: boolean;
        viewInternalNotes: boolean;
        editInternalNotes: boolean;
        viewNotesForClinician: boolean;
        editNotesForClinician: boolean;
    };
    documents: IDocumentListItem[];
    preferredTimeslots: WeekdayTimeWindow[];
    timezone: string | null;
}

const LabelTypography = ({ children }: { children: ReactNode }) => (
    <Typography
        sx={{ minWidth: '60px' }}
        variant='body2'
        fontSize={'12px'}
        lineHeight={'16px'}
        color={'#1E1E1E'}
    >
        {children}
    </Typography>
);

export const LeftRailTextButton = ({ sx, ...rest }: ButtonProps) => {
    return (
        <Button
            {...rest}
            size='small'
            variant='text'
            sx={{ color: '#08786C', textTransform: 'none', ...sx }}
        />
    );
};

export const LeftRailNoneLabel = ({ sx, ...rest }: NoneLabelProps) => {
    return <NoneLabel sx={{ fontSize: '14px', lineHeight: '20px', ...sx }} {...rest} />;
};

export const LeftRailDiscontinuedLabel = ({ sx, ...rest }: NoneLabelProps) => {
    return <LeftRailNoneLabel sx={sx} {...rest} />;
};

export const HeadingCaptionLabel = ({ children, ...rest }: TypographyProps) => (
    <CaptionLabel
        fontSize={'12px'}
        lineHeight={'16px'}
        color={'#1E1E1E'}
        fontWeight='500'
        {...rest}
    >
        {children}
    </CaptionLabel>
);

export const valueTypographyProps = {
    color: '#757575',
    fontSize: '14px',
    lineHeight: '20px',
};

export const LeftRailValueTypography = ({
    children,
    ...props
}: { children: ReactNode } & ComponentProps<typeof Typography>) => (
    <Typography variant='body2' {...valueTypographyProps} {...props}>
        {children}
    </Typography>
);

const PatientInfoFieldDisplay = ({
    label,
    value,
    noneLabelText,
}: {
    label: string;
    value: ReactNode;
    noneLabelText?: string;
}) => {
    const isStringValue = typeof value === 'string';

    return (
        <HorizontalStack spacing={4} alignItems='flex-start' fullWidth>
            <LabelTypography>{label}</LabelTypography>
            {value ? (
                <HorizontalStack spacing={1} alignItems='center'>
                    <LeftRailValueTypography>{value}</LeftRailValueTypography>
                    {isStringValue && (
                        <IconButton
                            size='small'
                            onClick={() => void copyToClipboard(value)}
                            sx={{ padding: '4px' }}
                        >
                            <ContentCopy sx={{ fontSize: '16px', color: '#757575' }} />
                        </IconButton>
                    )}
                </HorizontalStack>
            ) : (
                <LeftRailNoneLabel text={noneLabelText} />
            )}
        </HorizontalStack>
    );
};

const PatientNotes = ({ patient }: { patient: IPatientInfo }) => {
    const { id, notes, permissions } = patient;
    const [editPatientNotes] = useMutation(EDIT_PATIENT_NOTES_MUTATION);

    const handleSave = async (notesValue: string) => {
        await editPatientNotes({
            variables: {
                patientId: id,
                notes: notesValue,
            },
        });
    };

    return (
        <>
            <NotesEditor
                notes={notes}
                canView={permissions.viewInternalNotes}
                canEdit={permissions.editInternalNotes}
                onSave={handleSave}
                notesInfoIconProps={{
                    visibleToClinicians: false,
                    headline: 'Notes about the patient',
                    description:
                        'These notes will not be visible to clinicians; they will only be shown in the dashboard.',
                }}
                captionLabel={
                    <HeadingCaptionLabel>Patient Notes (Office Only)</HeadingCaptionLabel>
                }
            />
            <NotesEditor
                notes={patient.notesForClinician}
                canView={permissions.viewNotesForClinician}
                canEdit={permissions.editNotesForClinician}
                onSave={() => {
                    // ENG-7322: Nobody has permission to do this yet, so we throw for now.
                    throw new Error('TODO: Implement onSave for patient notes for clinicians');
                }}
                notesInfoIconProps={{
                    visibleToClinicians: true,
                    headline: 'Notes about the patient for clinicians',
                    description:
                        'These notes will be visible to clinicians who are assigned to the visit.',
                }}
                captionLabel={
                    <HeadingCaptionLabel>Patient Notes for Clinicians</HeadingCaptionLabel>
                }
            />
        </>
    );
};

const PatientStatusTag = ({ patient }: { patient: IPatientInfo }) => {
    const dashboardVariation = useDashboardVariationConfig();
    switch (dashboardVariation.variation) {
        case DashboardVariation.NORTH_STAR:
        case DashboardVariation.NORTH_STAR_UAT:
            return <NorthStarPatientStatusTag patientId={patient.id} />;
        default:
            return null;
    }
};

const PatientLeftRailContent = ({ patient }: { patient: IPatientInfo }) => {
    const { openDialog } = useDialog();
    const {
        externalId,
        externalIds,
        fullName,
        phoneNumber,
        email,
        dateOfBirth,
        sex,
        addresses,
        preferredLanguage,
        payers,
    } = patient;
    const isDemoOrg = useConfigValue('is_demo_org');
    const [isPatientDetailsExpanded, setIsPatientDetailsExpanded] = useState(false);

    const age = moment().diff(dateOfBirth, 'years');
    const dateOfBirthString = moment(dateOfBirth).format('MM/DD/YYYY');

    const singleExternalId = useMemo(() => {
        return externalIds?.[0]?.value ?? externalId;
    }, [externalIds, externalId]);

    return (
        <ThemeProvider theme={AxleV2Theme}>
            <VerticalStack>
                <VerticalStack
                    position='sticky'
                    top={0}
                    bgcolor='#2E5D5B'
                    p={3}
                    color='#f3f3f3'
                    gap={1}
                    zIndex={1}
                >
                    <HorizontalStack justifyContent='space-between' alignItems='flex-start'>
                        <VerticalStack>
                            <Link
                                component={RouterLink}
                                to={`/patients/${patient.id}`}
                                sx={{
                                    textDecoration: 'none',
                                    color: '#FFF',
                                    fontSize: '22px',
                                    lineHeight: '28px',
                                }}
                            >
                                {fullName}
                            </Link>
                            <Typography fontSize='14px' lineHeight='20px' fontWeight='500'>
                                {renderSex(sex)}, {age} years, {dateOfBirthString}
                            </Typography>
                        </VerticalStack>
                        <PatientStatusTag patient={patient} />
                    </HorizontalStack>
                </VerticalStack>
                <VerticalStack p={2} spacing={2}>
                    <VerticalStack spacing={2}>
                        <HorizontalStack justifyContent='space-between'>
                            <Typography
                                color='#08786C'
                                variant='h2'
                                fontSize='16px'
                                lineHeight='24px'
                                fontWeight='500'
                            >
                                Patient Details
                            </Typography>
                            <IconButton
                                onClick={() =>
                                    setIsPatientDetailsExpanded(!isPatientDetailsExpanded)
                                }
                            >
                                {isPatientDetailsExpanded ? (
                                    <ExpandLess sx={{ color: '#14AE5C' }} />
                                ) : (
                                    <ExpandMore sx={{ color: '#14AE5C' }} />
                                )}
                            </IconButton>
                        </HorizontalStack>
                        <VerticalStack spacing={2}>
                            <HorizontalStack spacing={4} alignItems='flex-start' fullWidth>
                                <LabelTypography>
                                    {addresses.length > 1 ? 'Locations' : 'Location'}
                                </LabelTypography>
                                <Box display='flex' flexDirection='column' gap={1}>
                                    {patient.addresses.length > 0 ? (
                                        patient.addresses.map((address) => (
                                            <LeftRailValueTypography key={address.id}>
                                                <AddressInTwoLines
                                                    address={basicAddressToAddress(address)}
                                                    shouldWrap={true}
                                                />
                                            </LeftRailValueTypography>
                                        ))
                                    ) : (
                                        <LeftRailNoneLabel />
                                    )}
                                </Box>
                            </HorizontalStack>
                            <PatientInfoFieldDisplay
                                label='Phone'
                                value={formatPhoneNumberForDisplay(phoneNumber)}
                            />
                            <PatientInfoFieldDisplay
                                label='External ID'
                                value={singleExternalId || <LeftRailNoneLabel />}
                            />
                            <Collapse in={isPatientDetailsExpanded}>
                                <VerticalStack spacing={2}>
                                    <PatientInfoFieldDisplay label='Email' value={email} />
                                    <PatientInfoFieldDisplay
                                        label='Language'
                                        value={renderLanguage(preferredLanguage)}
                                    />
                                    <PatientInfoFieldDisplay
                                        label='Payers'
                                        value={
                                            payers.length
                                                ? payers
                                                      .map((payer) => payer.name)
                                                      .sort((a, b) => a.localeCompare(b))
                                                      .join(', ')
                                                : 'None'
                                        }
                                    />
                                </VerticalStack>
                            </Collapse>
                        </VerticalStack>
                    </VerticalStack>
                    <Divider orientation='horizontal' flexItem />
                    <VerticalStack spacing={2}>
                        {patient.defaultEpisode && (
                            <PatientLeftRailEpisodeSection episode={patient.defaultEpisode} />
                        )}

                        {!isDemoOrg && <PatientCareTeamInfo patient={patient} />}
                        {isDemoOrg && (
                            <VerticalStack spacing={1}>
                                <HorizontalStack justifyContent='space-between'>
                                    <HeadingCaptionLabel>
                                        Preferred Availability
                                    </HeadingCaptionLabel>
                                    {patient.permissions.editDetails && (
                                        <LeftRailTextButton
                                            onClick={() =>
                                                openDialog(
                                                    DialogType.EditPatientTimeslotPreferences,
                                                    {
                                                        patientId: patient.id,
                                                    },
                                                )
                                            }
                                        >
                                            Edit
                                        </LeftRailTextButton>
                                    )}
                                </HorizontalStack>
                                {patient.preferredTimeslots.length > 0 ? (
                                    <PatientTimeslotPreferenceChipsStack
                                        patientTimeslotPreferences={patient.preferredTimeslots}
                                    />
                                ) : (
                                    <LeftRailNoneLabel />
                                )}
                            </VerticalStack>
                        )}

                        <DocumentViewerAndDropzone
                            documents={patient.documents}
                            canView={patient.permissions.viewDocuments}
                            canEdit={patient.permissions.editDocuments}
                            objectType='patient'
                            objectId={patient.id}
                            captionLabel='Documents'
                        />
                        <PatientNotes patient={patient} />
                    </VerticalStack>
                </VerticalStack>
            </VerticalStack>
        </ThemeProvider>
    );
};

export const PatientLeftRail = ({ patientId }: { patientId: string }) => {
    const { data } = useSuspenseQuery<{
        patient: IPatientInfo | null;
        organizations: { totalCount: number } | null;
    }>(PATIENT_LEFT_RAIL_QUERY, {
        variables: { patientId },
        errorPolicy: 'all',
    });

    useRedirectIfNotFound(
        !data?.patient,
        '/patients',
        "The patient you're looking for could not be found.",
    );

    if (!data?.patient) return null;

    return <PatientLeftRailContent patient={data.patient} />;
};
